import classNames from 'helpers/class-names'
import {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  FC,
  FocusEvent,
  FocusEventHandler,
  Fragment,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
  SVGProps,
  forwardRef,
} from 'react'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  type?: string
  name: string
  id: string
  placeholder?: string
  setValue: Dispatch<string>
  className?: string
  containerClassName?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  disabled?: boolean
  hasError?: boolean
  errorMessage?: ReactNode | string
  icon?: FC<SVGProps<SVGSVGElement>>
  iconClassName?: string
  prefix?: string
  suffix?: string
  note?: string
  noteClassName?: string
  variant?: 'compact' | 'normal' | 'large'
  textEnd?: boolean
  inputClassName?: string
}

export type InputProps = Props

export const Input = forwardRef(
  (
    {
      type = null,
      name,
      id,
      placeholder = '',
      value = '',
      setValue,
      className = '',
      containerClassName = '',
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      hasError = false,
      errorMessage = null,
      icon: IconComponent = Fragment,
      iconClassName = '',
      suffix = null,
      prefix = null,
      note = null,
      noteClassName = '',
      variant = 'normal',
      textEnd = false,
      inputClassName = '',
      ...props
    }: Props,
    ref: RefObject<HTMLInputElement>,
  ) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value)
      typeof onChange === 'function' && onChange(event)
    }

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      typeof onBlur === 'function' && onBlur(event)
    }

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      event.currentTarget.select()
      if (typeof onFocus === 'function') {
        onFocus(event)
      }
    }

    return (
      <div className={classNames('block w-full font-semibold', className)}>
        <div
          className={classNames(
            'relative flex  w-full flex-nowrap items-stretch',
            'border border-gray-200 bg-white ',
            'focus-within:border-info focus-within:ring-2 focus-within:ring-info/50',
            'dark:border-gray-700 dark:bg-gray-500',
            {
              'border-danger ring-2 ring-danger/50': hasError,
              'bg-gray-50 dark:bg-gray-700': disabled,
            },
            {
              'h-6 rounded text-sm': variant === 'compact',
              'h-7 rounded-md text-sm': variant === 'normal',
              'h-9 rounded-md text-lg': variant === 'large',
            },
            containerClassName,
          )}>
          {prefix && (
            <div
              className={classNames(
                'flex items-center  bg-gray-200  text-white dark:bg-gray-600',
                {
                  'rounded-l-sm px-1.5 text-xs': variant === 'compact',
                  'rounded-l px-1.5': variant === 'normal',
                  'rounded-l px-2': variant === 'large',
                },
              )}>
              {prefix}
            </div>
          )}
          {IconComponent !== Fragment && (
            <IconComponent
              className={classNames(
                'text-gray-200 ',
                {
                  'text-danger opacity-50 dark:opacity-100': hasError,
                },
                {
                  'ml-1 w-3.5': variant === 'compact',
                  '-mr-1 ml-1.5 w-4': variant === 'normal',
                  '-mr-1 ml-2 w-5': variant === 'large',
                },
                iconClassName,
              )}
            />
          )}
          <input
            type={type || 'text'}
            id={id}
            ref={ref}
            placeholder={placeholder}
            name={name}
            value={value}
            className={classNames(
              'w-px flex-1 grow bg-transparent leading-normal text-gray-400 ',
              'placeholder-gray-200 outline-none',
              'dark:text-gray-100 dark:placeholder-gray-300',
              { 'search-close-icon': type === 'search' },
              {
                'appearance-none': type === 'number',
              },
              {
                'cursor-not-allowed select-none text-gray-400 dark:text-gray-300':
                  disabled,
              },
              { 'text-end': textEnd },
              {
                'px-1': variant === 'compact',
                'pl-2 pr-1.5': variant === 'normal',
                'px-2': variant === 'large',
              },
              inputClassName,
            )}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            {...props}
          />
          {suffix && (
            <div
              className={classNames(
                'flex items-center  border-l border-gray-200 bg-gray-200 text-white dark:border-gray-700 dark:bg-gray-600',
                {
                  'rounded-r-sm px-1.5 text-xs': variant === 'compact',
                  'rounded-r px-1.5 ': variant === 'normal',
                  'rounded-r px-2': variant === 'large',
                },
              )}>
              {suffix}
            </div>
          )}
        </div>
        {typeof errorMessage === 'string' ? (
          <div
            className={classNames('mt-1 text-danger', {
              'text-xs': variant === 'normal' || variant === 'compact',
              'text-sm': variant === 'large',
            })}>
            {errorMessage}
          </div>
        ) : (
          errorMessage
        )}
      </div>
    )
  },
)
